/**
 * makeupService — the customer-facing half of the makeup-credit system
 * (Track 2 Slice C, fall model §3f/§4).
 *
 * Two callables (cancelLesson / redeemMakeupCredit) — authoritative writes;
 * the server decides the 24h outcome and enforces family ownership inside the
 * transaction. Clients never write timeSlots / makeupCredits / makeupBalance
 * directly. Plus one read: the open makeup pool.
 */

import { db, functions } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Cancel one occurrence of a lesson. The SERVER decides credit eligibility
// (24h boundary in studio time) — returns { outcome: 'credit_issued' |
// 'no_credit' | 'already_cancelled', ... }; key UI copy on `outcome`, never
// on a client-side clock. For a redeemed makeup / one-time lesson, `date`
// is the slot's specificDate. `note` (v1.1, optional) is the family's
// free-text — stored on the cancellation record and pushed to the owners;
// it never changes the outcome.
//
// `by: 'cyv'` (admins only; the server refuses anyone else, 2026-10-08): the
// STUDIO is cancelling. Outcome 'cyv_cancelled': the family always gets one
// makeup credit (from the session record), and the time is not opened for
// makeups. Omitted = the family's cancellation, exactly as before.
export async function cancelLesson(slotId, date, note, by) {
	const callable = httpsCallable(functions, 'cancelLesson');
	const payload = note ? { slotId, date, note } : { slotId, date };
	if (by === 'cyv') payload.by = 'cyv';
	const res = await callable(payload);
	return res.data;
}

// Spend one of `studentId`'s makeup credits on an open makeup slot. First
// caller wins a race; the loser gets HttpsError 'failed-precondition'
// ("just booked") and should refresh the pool.
export async function redeemMakeupCredit(makeupSlotId, studentId) {
	const callable = httpsCallable(functions, 'redeemMakeupCredit');
	const res = await callable({ makeupSlotId, studentId });
	return res.data;
}

// The open makeup pool. This query's TWO equality constraints are load-bearing,
// not a filter: the customer read rule grants exactly
// `status:'open' && isMakeup:true` (family-agnostic), and RULES ARE NOT
// FILTERS — dropping either where() gets the whole query denied (the
// workshops/packs + Song Notes trap). Equality-only, so no composite index
// needed. Callers filter past dates client-side.
export async function fetchOpenMakeupSlots() {
	const q = query(
		collection(db, 'timeSlots'),
		where('status', '==', 'open'),
		where('isMakeup', '==', true)
	);
	const snapshot = await getDocs(q);
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}
